import React, { useState, useEffect } from 'react';
import { bookingAPI, eventBookingAPI } from '../../utils/api';
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import toast from '../../utils/notifications';
import { useAuth } from '../../context/AuthContext';
import { dashboardRefreshEmitter } from '../../utils/dashboardRefresh';

const BookingsManagement = () => {
  const { user, isAdmin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [eventBookings, setEventBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [bookingType, setBookingType] = useState('all'); // 'all', 'room', 'event'
  const [deleteModal, setDeleteModal] = useState({ show: false, bookingId: null, type: null }); // type: 'room' or 'event'

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      // Fetch room bookings
      const roomResponse = await bookingAPI.getAll({ per_page: 10000 });
      let roomBookingsData = roomResponse.data.data?.data || roomResponse.data.data || [];
      if (roomBookingsData && !Array.isArray(roomBookingsData) && roomBookingsData.data) {
        roomBookingsData = roomBookingsData.data;
      }
      setBookings(Array.isArray(roomBookingsData) ? roomBookingsData : []);

      // Fetch event bookings
      const eventResponse = await eventBookingAPI.getAll({ per_page: 10000 });
      let eventBookingsData = eventResponse.data.data?.data || eventResponse.data.data || [];
      if (eventBookingsData && !Array.isArray(eventBookingsData) && eventBookingsData.data) {
        eventBookingsData = eventBookingsData.data;
      }
      setEventBookings(Array.isArray(eventBookingsData) ? eventBookingsData : []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
      setEventBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await bookingAPI.updateStatus(bookingId, newStatus);
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
      // Trigger dashboard refresh
      dashboardRefreshEmitter.emit();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking status');
    }
  };

  const updateEventBookingStatus = async (bookingId, newStatus) => {
    try {
      await eventBookingAPI.updateStatus(bookingId, newStatus);
      toast.success(`Event booking status updated to ${newStatus}`);
      fetchBookings();
      // Trigger dashboard refresh
      dashboardRefreshEmitter.emit();
    } catch (error) {
      console.error('Error updating event booking:', error);
      toast.error('Failed to update event booking status');
    }
  };

  const deleteBooking = (bookingId) => {
    setDeleteModal({ show: true, bookingId, type: 'room' });
  };

  const deleteEventBooking = (bookingId) => {
    setDeleteModal({ show: true, bookingId, type: 'event' });
  };

  const confirmDelete = async () => {
    try {
      if (deleteModal.type === 'room') {
        console.log('Deleting booking:', deleteModal.bookingId);
        await bookingAPI.delete(deleteModal.bookingId);
        console.log('Booking deleted successfully from API');
        setBookings(bookings.filter(b => b.booking_id !== deleteModal.bookingId));
        console.log('Booking removed from state');
        toast.success('Booking deleted successfully');
      } else {
        await eventBookingAPI.delete(deleteModal.bookingId);
        setEventBookings(eventBookings.filter(b => b.event_booking_id !== deleteModal.bookingId));
        toast.success('Event booking deleted successfully');
      }
      setDeleteModal({ show: false, bookingId: null, type: null });
      setTimeout(() => {
        console.log('Emitting dashboard refresh');
        dashboardRefreshEmitter.emit();
      }, 300);
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const filteredEventBookings = eventBookings.filter((booking) => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const displayBookings = bookingType === 'all' ? [...filteredEventBookings, ...filteredBookings]
                        : bookingType === 'event' ? filteredEventBookings
                        : filteredBookings;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Bookings Management</h2>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">View and manage all reservations</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-3 sm:p-4">
        <div className="mb-4">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Booking Type</h3>
          <div className="flex flex-wrap gap-2">
            {['all', 'room', 'event'].map((type) => (
              <button
                key={type}
                onClick={() => setBookingType(type)}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                  bookingType === type
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'All Bookings' : type === 'room' ? 'Room Bookings' : 'Event Bookings'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Status Filter</h3>
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                  filter === status
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bookings Grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {displayBookings.length === 0 ? (
          <div className="col-span-full text-center py-8 sm:py-12 bg-white shadow rounded-lg">
            <p className="text-sm sm:text-base text-gray-500">No bookings found</p>
          </div>
        ) : (
          displayBookings.map((booking) => {
            const isEventBooking = !!booking.event_booking_id;
            
            if (isEventBooking) {
              return (
                <div key={booking.event_booking_id} className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4 gap-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                        Event #{booking.event_booking_id}
                      </h3>
                      <span
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {booking.status}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Event Type:</p>
                        <p className="truncate">{booking.event_type}</p>
                      </div>

                      <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-700">Event Date:</p>
                          <p className="truncate">{booking.event_date}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                        <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                        <span>{booking.number_of_guests} guest{booking.number_of_guests > 1 ? 's' : ''}</span>
                      </div>

                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Guest Name:</p>
                        <p className="truncate">{booking.first_name} {booking.last_name}</p>
                      </div>

                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Contact:</p>
                        <p className="truncate">{booking.email}</p>
                        <p className="truncate">{booking.phone}</p>
                      </div>

                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Total Amount:</p>
                        <p className="text-base sm:text-lg font-semibold text-black">₱{parseFloat(booking.total_price).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>

                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Payment Option:</p>
                        <p className="truncate">{booking.payment_option}</p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-2">
                      <select
                        value={booking.status}
                        onChange={(e) => updateEventBookingStatus(booking.event_booking_id, e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      {isAdmin() && (
                        <button
                          onClick={() => deleteEventBooking(booking.event_booking_id)}
                          className="w-full px-2 sm:px-3 py-2 bg-red-600 text-white rounded-md text-xs sm:text-sm font-medium hover:bg-red-700 transition-colors"
                        >
                          Delete Booking
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div key={booking.booking_id} className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4 gap-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                        Booking #{booking.booking_id}
                      </h3>
                      <span
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {booking.status}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Service:</p>
                        <p className="truncate">{booking.service_type}</p>
                      </div>

                      <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-700">Check-in:</p>
                          <p className="truncate text-xs">{new Date(booking.booking_time_start).toLocaleString('en-PH', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</p>
                          <p className="font-medium text-gray-700 mt-1">Check-out:</p>
                          <p className="truncate text-xs">{new Date(booking.booking_time_end).toLocaleString('en-PH', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                        <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                        <span>{booking.party_size} guest{booking.party_size > 1 ? 's' : ''}</span>
                      </div>

                      {/* Order Items (if available) */}
                      {booking.order_items && booking.order_items.length > 0 && (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <p className="font-medium text-gray-700">Order Items:</p>
                          <div className="space-y-1 mt-1">
                            {booking.order_items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-xs text-gray-600">
                                <span>{item.quantity}x {item.menu_item?.name || item.name || 'Item'}</span>
                                <span>₱{parseFloat(item.price * item.quantity).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="text-xs sm:text-sm text-gray-600">
                        <p className="font-medium text-gray-700">Total Amount:</p>
                        <p className="text-base sm:text-lg font-semibold text-black">₱{parseFloat(booking.total_amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>

                      {booking.special_requests && (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <p className="font-medium text-gray-700">Special Requests:</p>
                          <p className="text-gray-500 line-clamp-2">{booking.special_requests}</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 space-y-2">
                      <select
                        value={booking.status}
                        onChange={(e) => updateBookingStatus(booking.booking_id, e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      {isAdmin() && (
                        <button
                          onClick={() => deleteBooking(booking.booking_id)}
                          className="w-full px-2 sm:px-3 py-2 bg-red-600 text-white rounded-md text-xs sm:text-sm font-medium hover:bg-red-700 transition-colors"
                        >
                          Delete Booking
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this {deleteModal.type === 'event' ? 'event' : ''} booking? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, bookingId: null, type: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsManagement;

